import _ from 'lodash';
import React, { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';

import analytics from 'scripts/lib/analytics';
import { ComposerContextProvider } from 'components/composer/contexts/composer_context';
import CompositionContentType from 'models/composition/composition_content_type';
import CompositionModel from 'models/composition';
import connect from 'components/lib/connect';
import Err from 'models/err';
import ErrorBoundary from 'components/common/utilities/error_boundary';
import ChatComposer from 'components/composer/chat_composer';
import ConversationMessageComposer from 'components/composer/conversation_message_composer';
import CreditCardComposer from 'components/composer/credit_card_composer';
import EmailComposer from 'components/composer/email_composer';
import ExternalFormContainer from './composition/external_form_container';
import FbMessengerComposer from 'components/composer/fb_messenger_composer';
import NoteComposer from 'components/composer/note_composer';
import PhoneCallCompositionContainer from './composition/phone_call_composition_container';
import registerHotkey from 'components/hotkeys/register_hotkey';
import SmsComposer from 'components/composer/sms_composer';
import TaskComposer from 'components/composer/task_composer';
import ToggleAnswerPanel from 'actions/answers/toggle_answer_panel';
import ToggleComposerVisibility from 'actions/composer/toggle_composer_visibility';
import { useExecuteAction } from 'components/hooks/connect_hooks';
import useKeyboardShortcut from 'components/hooks/use_keyboard_shortcut';

const toggleComposerVisibilityHotkey = registerHotkey({
  key: 'alt+h',
  label: 'Show/hide composition',
  group: 'Customer',
});

export function Composition(props) {
  const executeAction = useExecuteAction();
  const toggleComposerVisibility = useCallback(() => executeAction(ToggleComposerVisibility), [executeAction]);
  useKeyboardShortcut(toggleComposerVisibilityHotkey, toggleComposerVisibility);

  const { composition, hasChannelConfiguration, isCompositionMinimized } = props;
  if (!composition || !hasChannelConfiguration) {
    return null;
  }

  const contentType = composition.contentType();
  const style = isCompositionMinimized ? { display: 'none' } : {};

  // Set key on div so composition fully unmounts / remounts when composition changes
  return (
    <div className="customerComposition-shadowWrapper" style={style}>
      <div className={`conversationResponse-composition-container ${_.kebabCase(contentType)}`}>
        <div className="composition" key={composition.id}>
          <ErrorBoundary
            renderError={({ onResolveError }) => (
              <CompositionError onClickContinue={onResolveError} composition={composition} />
            )}
          >
            {getForm()}
          </ErrorBoundary>
        </div>
      </div>
    </div>
  );

  function getForm() {
    const {
      compositionErrors,
      onMount,
      onToggleAnswerPanel,
      selectedKbItem,
      selectedKbItemChannelType,
      selectedKbItemId,
      selectedKbItemLanguage,
    } = props;
    let defaultProps = {
      composition,
      compositionErrors,
      isMinimized: isCompositionMinimized,
      onMount,
      selectedKbItem,
      selectedKbItemChannelType,
      selectedKbItemId,
      selectedKbItemLanguage,
      toggleAnswerPanel: onToggleAnswerPanel,
    };

    switch (contentType) {
      /* Slate composers */

      case CompositionContentType.CHAT:
        return (
          <ComposerContextProvider composition={composition}>
            <ChatComposer />
          </ComposerContextProvider>
        );

      case CompositionContentType.CONVERSATION_NOTE:
        return (
          <ComposerContextProvider composition={composition}>
            <NoteComposer />
          </ComposerContextProvider>
        );

      case CompositionContentType.CONVERSATION_MESSAGE:
        return (
          <ComposerContextProvider composition={composition}>
            <ConversationMessageComposer />
          </ComposerContextProvider>
        );

      case CompositionContentType.CREDIT_CARD_REQUEST:
        return (
          <ComposerContextProvider composition={composition}>
            <CreditCardComposer />
          </ComposerContextProvider>
        );

      case CompositionContentType.EMAIL:
        return (
          <ComposerContextProvider composition={composition}>
            <EmailComposer />
          </ComposerContextProvider>
        );

      case CompositionContentType.FB_MESSAGE:
        return (
          <ComposerContextProvider composition={composition}>
            <FbMessengerComposer />
          </ComposerContextProvider>
        );

      case CompositionContentType.TASK:
        return (
          <ComposerContextProvider composition={composition}>
            <TaskComposer />
          </ComposerContextProvider>
        );

      case CompositionContentType.TASK_EDIT:
        return (
          <ComposerContextProvider composition={composition}>
            <TaskComposer isEditing />
          </ComposerContextProvider>
        );

      case CompositionContentType.SMS:
        return (
          <ComposerContextProvider composition={composition}>
            <SmsComposer />
          </ComposerContextProvider>
        );

      /* Draft composition forms */

      case CompositionContentType.EXTERNAL_FORM:
        return <ExternalFormContainer {...defaultProps} />;

      case CompositionContentType.PHONE_CALL:
        return <PhoneCallCompositionContainer {...defaultProps} />;

      default:
        return null;
    }
  }
}

Composition.propTypes = {
  composition: PropTypes.instanceOf(CompositionModel),
  compositionErrors: PropTypes.arrayOf(PropTypes.instanceOf(Err)),
  hasChannelConfiguration: PropTypes.bool,
  isCompositionMinimized: PropTypes.bool,
  selectedKbItem: PropTypes.object,
  selectedKbItemId: PropTypes.string,
  selectedKbItemChannelType: PropTypes.string,

  onToggleAnswerPanel: PropTypes.func.isRequired,
};

function CompositionError({ onClickContinue, composition }) {
  useEffect(() => {
    analytics.track('Whoops Screen Shown', {
      componentName: 'composition',
      compositionId: composition.id,
      conversationId: composition.conversationId,
      customerId: composition.customerId,
      contentType: composition.contentType(),
    });
  }, [composition, composition.id]);
  return (
    <div className="composition-errorBoundary">
      <div>Whoops! Something went wrong.</div>
      <div className="composition-errorBoundary-continue" onClick={onClickContinue}>
        Click here to continue editing
      </div>
    </div>
  );
}

CompositionError.propTypes = {
  onClickContinue: PropTypes.func.isRequired,
  composition: PropTypes.object,
};

const CompositionContainer = connect(mapStateToProps, mapExecuteToProps)(Composition);
CompositionContainer.propTypes = {
  compositionId: PropTypes.string,
  onSubmit: PropTypes.func,
};
export default CompositionContainer;

function mapStateToProps({ getProvider, isFeatureEnabled }, { currentCompositionId, onMount }) {
  const hasChannelConfiguration = !!getProvider('channelConfiguration').get();
  const compositionsProvider = getProvider('compositions');

  const composition = compositionsProvider.findBy({ id: currentCompositionId });
  const compositionErrors = (composition && compositionsProvider.getErrors(composition.id)) || [];

  const selectedKbItem = getProvider('selectedKbItem').get();
  const { isCompositionMinimized } = getProvider('currentLocation').get();

  return {
    composition,
    compositionErrors,
    hasChannelConfiguration,
    isCompositionMinimized,
    onMount,
    selectedKbItem: _.get(selectedKbItem, 'item'),
    selectedKbItemId: _.get(selectedKbItem, 'id'),
    selectedKbItemChannelType: _.get(selectedKbItem, 'channelType'),
    selectedKbItemLanguage: _.get(selectedKbItem, 'language'),
  };
}

function mapExecuteToProps(executeAction) {
  return {
    onToggleAnswerPanel: attrs => executeAction(ToggleAnswerPanel, attrs),
  };
}



// WEBPACK FOOTER //
// ./src/scripts/presentation/components/customer/composition.jsx